import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';

import type { Experience as BuilderExperience } from 'app/typings/builder/experience';
import type { Experience } from 'app/typings/experiences';
import { apiMutation, apiRequest } from 'app/utils/request/api';

const pathHotelExperience = 'hotels/:hotelId/experiences' as const;
const saveExperiencePath = 'experiences' as const;

export function useHotelExperiences(
  hotelId: number | undefined,
  queryParams: { includeStaycationExp: boolean }
) {
  const queryKey = [pathHotelExperience, { hotelId, queryParams }];

  return useQuery({
    queryKey: queryKey,
    queryFn: () =>
      hotelId
        ? (apiRequest({
            path: pathHotelExperience,
            params: { hotelId: hotelId.toString() },
            queryParams,
          }).catch(() => {
            message.error('Error fetching hotel experiences');

            return [];
          }) as Promise<Experience[]>)
        : Promise.resolve([]),
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!hotelId,
  });
}

export function useSaveHotelExperience() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: BuilderExperience) =>
      apiMutation(
        'POST',
        { path: saveExperiencePath },
        payload
      ) as Promise<number>,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [pathHotelExperience] });
    },
    onError: () => {
      message.error('Error saving Experience');
    },
  });
}
